import React, { useState } from 'react';
import './ZeroHarm.css';

const ZeroHarm = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedPillars, setExpandedPillars] = useState({
    safety: true,
    welfare: false,
    environment: false,
    emergency: false,
    training: false,
    asset: false,
  });
  const [showGoldenRulesModal, setShowGoldenRulesModal] = useState(false);

  const togglePillar = (key) => {
    setExpandedPillars((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const metrics = [
    {
      value: '0',
      title: 'Target Incidents',
      desc: 'Zero Lost Time Incidents (LTI) standard across all fleet voyages & dockyards.',
    },
    {
      value: '100%',
      title: 'Stop Work Authority',
      desc: 'Universal empowerment to halt any operation deemed unsafe without repercussion.',
    },
    {
      value: '24/7',
      title: 'Emergency Oversight',
      desc: 'Round-the-clock marine response team, remote monitoring & medical coverage.',
    },
    {
      value: 'ISO',
      title: '45001 & 14001',
      desc: 'Audited alignment with global occupational health, safety & environmental standards.',
    },
  ];

  const pillars = [
    {
      id: 'safety',
      category: 'people',
      tag: 'Safety Culture',
      title: 'Occupational Safety & Risk Prevention',
      description:
        'We enforce proactive hazard detection and standardized safety routines, turning every crew member into a dedicated safety leader.',
      protocols: [
        'Mandatory daily Toolbox Talks & Dynamic Risk Assessments (DRA)',
        'Unconditional Stop Work Authority (SWA) for all ranks',
        'Behaviour-Based Safety (BBS) audits and transparent near-miss reporting',
        'Strict Permit-to-Work (PTW) system for hot work & confined spaces',
      ],
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      ),
    },
    {
      id: 'welfare',
      category: 'people',
      tag: 'Seafarer Care',
      title: 'Crew Welfare, Health & Mental Well-being',
      description:
        'A resilient maritime operation begins with the physical vitality and psychological comfort of our seafarers at sea and in port.',
      protocols: [
        'Comprehensive pre-voyage medicals and 24/7 offshore tele-medical support',
        'Confidential mental health assistance and fatigue risk management',
        'Strict compliance with Maritime Labour Convention (MLC 2006) rest hours',
        'Ergonomic living quarters and nutritious dietary standards onboard',
      ],
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
          <path d="M12 5v14" />
          <path d="M5 12h14" />
        </svg>
      ),
    },
    {
      id: 'environment',
      category: 'environment',
      tag: 'Ecosystem Care',
      title: 'Marine Environmental Stewardship',
      description:
        'Protecting our oceans and coastal biodiversity through rigorous zero-spill protocols, emissions reduction, and green voyage practices.',
      protocols: [
        'Zero-discharge policy & advanced oily water separator (OWS) verification',
        'Full MARPOL Annex I through VI environmental compliance',
        'Ballast Water Management System (BWMS) to prevent invasive species',
        'Eco-certified hull coatings and non-hazardous biodegradable consumables',
      ],
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 12h1a2 2 0 0 1 1.8 1.1l.4.9a2 2 0 0 0 1.8 1h10a2 2 0 0 0 1.8-1l.4-.9A2 2 0 0 1 21 12h1" />
          <path d="M2 17h1a2 2 0 0 1 1.8 1.1l.4.9a2 2 0 0 0 1.8 1h10a2 2 0 0 0 1.8-1l.4-.9A2 2 0 0 1 21 17h1" />
          <path d="M12 3a7 7 0 0 0-7 7c0 4 7 10 7 10s7-6 7-10a7 7 0 0 0-7-7Z" />
          <circle cx="12" cy="10" r="2.5" />
        </svg>
      ),
    },
    {
      id: 'emergency',
      category: 'emergency',
      tag: 'Crisis Response',
      title: 'Emergency Preparedness & Crisis Mitigation',
      description:
        'Prepared for any maritime contingency through regular realistic simulations, dedicated incident command, and modern survival equipment.',
      protocols: [
        'Weekly live-fire, vessel evacuation, and man-overboard (MOB) drills',
        'Shore-based 24/7 Emergency Response Team (ERT) incident room',
        'Satellite distress integration with international Maritime Rescue Centers (MRCC)',
        'Strategic prepositioning of marine spill control & salvage gear',
      ],
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="4" />
          <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
          <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
          <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
          <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
        </svg>
      ),
    },
    {
      id: 'training',
      category: 'people',
      tag: 'Quality & Audit',
      title: 'Continuous STCW Training & Compliance',
      description:
        'Continuous qualification frameworks ensure all officers, engineers, and shore personnel exceed global regulatory benchmarks.',
      protocols: [
        'STCW, ISM, and ISPS Code compliance certifications',
        'Full-mission bridge and engine room simulation drills',
        'Scheduled internal safety audits and third-party classification reviews',
        'Transparent QHSE KPI scorecards and continuous corrective actions',
      ],
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="6" />
          <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
        </svg>
      ),
    },
    {
      id: 'asset',
      category: 'emergency',
      tag: 'Asset Safety',
      title: 'Asset Integrity & Technical Reliability',
      description:
        'Maintaining vessel hull soundness, propulsion machinery, and electrical systems to prevent failures before they emerge.',
      protocols: [
        'Predictive condition monitoring & vibration analysis on critical machinery',
        'Non-Destructive Testing (NDT) and ultrasonic hull thickness inspections',
        'Class society survey readiness & preventive drydock planning',
        'Comprehensive spare parts management & redundancy verification',
      ],
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
        </svg>
      ),
    },
  ];

  const filteredPillars =
    activeCategory === 'all'
      ? pillars
      : pillars.filter((p) => p.category === activeCategory);

  const goldenRules = [
    {
      number: '01',
      title: 'Assess Hazards Before Execution',
      desc: 'Never start a task without an approved Permit-to-Work, Toolbox Talk, and verified Dynamic Risk Assessment.',
    },
    {
      number: '02',
      title: 'Always Exercise Stop Work Authority (SWA)',
      desc: 'If anything feels abnormal, unplanned, or hazardous, STOP immediately. Your intervention is always backed by management.',
    },
    {
      number: '03',
      title: 'Zero Tolerance for Confined Space & Hot Work Bypasses',
      desc: 'Test atmospheres with calibrated multi-gas detectors and verify continuous positive ventilation and firefighting standby.',
    },
    {
      number: '04',
      title: 'Mandatory Certified PPE at All Times',
      desc: 'Wear certified task-specific personal protective equipment, lifejackets near waterlines, and double-lanyard fall arresters at heights.',
    },
    {
      number: '05',
      title: 'Zero Spills & Immediate Hazard Reporting',
      desc: 'Report all near-misses, unaddressed defects, and environmental risks instantly so corrective actions prevent actual harm.',
    },
  ];

  return (
    <section className="zh-section" id="zero-harm">
      {/* Decorative ambient glowing orbs */}
      <div className="zh-bg-glow zh-bg-glow-1"></div>
      <div className="zh-bg-glow zh-bg-glow-2"></div>

      <div className="zh-container">
        {/* Header Section */}
        <div className="zh-header">
          <div className="zh-badge-wrapper">
            <span className="zh-badge-dot"></span>
            <span className="zh-badge-text">QHSE EXCELLENCE & CULTURE</span>
          </div>

          <h2 className="zh-title">
            Target Zero: Our Commitment to{' '}
            <span className="zh-title-highlight">Zero Harm</span>
          </h2>

          <p className="zh-subtitle">
            In ship management, offshore engineering, and afloat repairs, safety is never a compromise. We empower every individual to champion life, protect our fleet, and preserve the marine environment.
          </p>
        </div>

        {/* Metrics Bar */}
        <div className="zh-metrics-grid">
          {metrics.map((metric, idx) => (
            <div key={idx} className="zh-metric-card">
              <div className="zh-metric-val">{metric.value}</div>
              <div className="zh-metric-title">{metric.title}</div>
              <p className="zh-metric-desc">{metric.desc}</p>
            </div>
          ))}
        </div>

        {/* Category Tabs */}
        <div className="zh-tabs-container">
          <button
            className={`zh-tab-btn ${activeCategory === 'all' ? 'active' : ''}`}
            onClick={() => setActiveCategory('all')}
          >
            <span>All Pillars</span>
            <span className="zh-tab-count">({pillars.length})</span>
          </button>
          <button
            className={`zh-tab-btn ${activeCategory === 'people' ? 'active' : ''}`}
            onClick={() => setActiveCategory('people')}
          >
            <span>Crew & Personnel Safety</span>
          </button>
          <button
            className={`zh-tab-btn ${activeCategory === 'environment' ? 'active' : ''}`}
            onClick={() => setActiveCategory('environment')}
          >
            <span>Marine Environmental Care</span>
          </button>
          <button
            className={`zh-tab-btn ${activeCategory === 'emergency' ? 'active' : ''}`}
            onClick={() => setActiveCategory('emergency')}
          >
            <span>Emergency Readiness</span>
          </button>
        </div>

        {/* Pillars Grid */}
        <div className="zh-pillars-grid">
          {filteredPillars.map((pillar) => {
            const isOpen = !!expandedPillars[pillar.id];
            return (
              <div key={pillar.id} className="zh-card">
                <div className="zh-card-top">
                  <div className="zh-icon-bubble">{pillar.icon}</div>
                  <span className="zh-tag">{pillar.tag}</span>
                </div>

                <h3 className="zh-card-title">{pillar.title}</h3>
                <p className="zh-card-desc">{pillar.description}</p>

                <div className="zh-protocols-wrapper">
                  <div
                    className="zh-protocols-heading"
                    onClick={() => togglePillar(pillar.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && togglePillar(pillar.id)}
                  >
                    <span>Key Protocols & Safeguards</span>
                    <svg
                      className={`zh-chevron ${isOpen ? 'rotated' : ''}`}
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>

                  <ul className={`zh-protocols-list ${isOpen ? 'open' : ''}`}>
                    {pillar.protocols.map((protocol, pIdx) => (
                      <li key={pIdx} className="zh-protocol-item">
                        <svg
                          className="zh-check-icon"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span>{protocol}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* SWA (Stop Work Authority) Callout Banner */}
        <div className="zh-swa-banner">
          <div className="zh-swa-left">
            <div className="zh-swa-icon-box">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" />
                <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" />
                <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8" />
                <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
              </svg>
            </div>
            <div className="zh-swa-content">
              <h3>
                Universal Stop Work Authority (SWA)
                <span className="zh-swa-badge">Zero Compromise</span>
              </h3>
              <p>
                Every employee, technician, and subcontractor aboard our vessels or shipyards holds the authority and moral obligation to halt any operation if an unsafe condition exists. Safety always supersedes schedule.
              </p>
            </div>
          </div>
          <button
            className="zh-swa-action-btn"
            onClick={() => setShowGoldenRulesModal(true)}
          >
            <span>Our 5 Golden Safety Rules</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      </div>

      {/* Modal: 5 Golden Safety Rules */}
      {showGoldenRulesModal && (
        <div
          className="zh-modal-overlay"
          onClick={() => setShowGoldenRulesModal(false)}
        >
          <div
            className="zh-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="zh-modal-close"
              onClick={() => setShowGoldenRulesModal(false)}
              aria-label="Close modal"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="zh-modal-header">
              <h3 className="zh-modal-title">Our 5 Golden Safety Rules</h3>
              <p className="zh-modal-desc">
                Core, mandatory operating principles that apply across all BlueWave marine assets, offshore operations, and drydock projects.
              </p>
            </div>

            <div className="zh-rules-list">
              {goldenRules.map((rule) => (
                <div key={rule.number} className="zh-rule-item">
                  <div className="zh-rule-num">{rule.number}</div>
                  <div className="zh-rule-info">
                    <h4>{rule.title}</h4>
                    <p>{rule.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ZeroHarm;